import { Component, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

/**
 * Toggle option interface
 */
export interface FancyToggleOption {
  value: string;
  label: string;
  icon?: string;
}

/**
 * Fancy Button Toggle Component
 *
 * A modern toggle button group with icon support and smooth animations.
 * Follows the project's design system with BEM naming and theme support.
 *
 * @example
 * ```html
 * <fancy-button-toggle
 *   [options]="statusOptions"
 *   [value]="selectedStatus()"
 *   (valueChange)="onStatusChange($event)">
 * </fancy-button-toggle>
 * ```
 *
 * ```typescript
 * statusOptions: FancyToggleOption[] = [
 *   { value: 'open', label: 'Abiertas', icon: 'lock_open' },
 *   { value: 'closed', label: 'Cerradas', icon: 'lock' },
 *   { value: 'all', label: 'Todas', icon: 'all_inclusive' }
 * ];
 * ```
 */
@Component({
  selector: 'fancy-button-toggle',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule],
  styleUrl: './fancy-button-toggle.component.scss',
  template: `
    <div
      class="fancy-button-toggle"
      [class.fancy-button-toggle--disabled]="disabled()"
      [class.fancy-button-toggle--sm]="size() === 'sm'"
      [class.fancy-button-toggle--md]="size() === 'md'"
      [class.fancy-button-toggle--lg]="size() === 'lg'"
    >
      @for (option of options(); track option.value) {
        <button
          type="button"
          class="fancy-button-toggle__button"
          [class.fancy-button-toggle__button--selected]="value() === option.value"
          [disabled]="disabled()"
          (click)="selectOption(option.value)"
        >
          @if (option.icon) {
            <mat-icon class="fancy-button-toggle__icon">
              {{ option.icon }}
            </mat-icon>
          }
          @if (showLabels()) {
            <span class="fancy-button-toggle__label">
              {{ option.label }}
            </span>
          }
        </button>
      }
    </div>
  `,
})
export class FancyButtonToggleComponent {
  /**
   * Toggle options array
   */
  options = input.required<FancyToggleOption[]>();

  /**
   * Current selected value
   */
  value = input<string>('');

  /**
   * Disabled state
   */
  disabled = input<boolean>(false);

  /**
   * Show labels on buttons (hide on mobile if false)
   */
  showLabels = input<boolean>(true);

  /**
   * Button size
   */
  size = input<'sm' | 'md' | 'lg'>('md');

  /**
   * Value change event
   */
  valueChange = output<string>();

  /**
   * Select an option
   */
  selectOption(value: string): void {
    if (!this.disabled() && value !== this.value()) {
      this.valueChange.emit(value);
    }
  }
}
